import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import Seo from "@/components/Seo";
import { trackProductView } from "@/lib/attribution";

const money = (c) => `$${(c / 100).toFixed(2)}`;

function Field({ f, value, onChange }) {
  if (f.type === "select") {
    return (
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`field-${f.key}`}
        className="w-full bg-white border-2 border-[color:var(--vt-dark)] px-4 py-3 text-sm focus:outline-none focus:border-[color:var(--vt-amber)]">
        <option value="">Select…</option>
        {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  if (f.type === "text") {
    return <input type="text" maxLength={f.max || 60} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`field-${f.key}`}
      className="w-full bg-white border-2 border-[color:var(--vt-dark)] px-4 py-3 text-sm focus:outline-none focus:border-[color:var(--vt-amber)]" />;
  }
  if (f.type === "textarea") {
    const len = (value || "").length;
    return (
      <div>
        <p className="text-xs text-[color:var(--vt-label-on-cream)] mb-1">Don't include names, schools, or contact info — we don't need them.</p>
        <textarea maxLength={f.max || 600} rows={4} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`field-${f.key}`}
          className="w-full bg-white border-2 border-[color:var(--vt-dark)] px-4 py-3 text-sm focus:outline-none focus:border-[color:var(--vt-amber)]" />
        <p className="text-xs text-[color:var(--vt-graphite)] text-right">{len}/{f.max || 600}</p>
      </div>
    );
  }
  // chips / chips_multi
  const multi = f.type === "chips_multi";
  const arr = multi ? (value || []) : value;
  const toggle = (o) => {
    if (multi) { const s = new Set(arr); s.has(o) ? s.delete(o) : s.add(o); onChange([...s]); }
    else onChange(o);
  };
  return (
    <div className="flex flex-wrap gap-2" data-testid={`field-${f.key}`}>
      {f.options.map((o) => {
        const on = multi ? arr.includes(o) : arr === o;
        return (
          <button key={o} type="button" onClick={() => toggle(o)} data-testid={`chip-${f.key}-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            className={`font-head uppercase tracking-wide text-xs px-4 py-2 border-2 transition-colors ${on ? "bg-[color:var(--vt-dark)] text-[color:var(--vt-cream)] border-[color:var(--vt-dark)]" : "border-[color:var(--vt-dark)] text-[color:var(--vt-body-on-cream)] hover:bg-[color:var(--vt-dark)]/5"}`}>
            {o}
          </button>
        );
      })}
    </div>
  );
}

export default function QuickPlay() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [inputs, setInputs] = useState({});
  const [purchaser, setPurchaser] = useState(null);
  const [step, setStep] = useState("form");
  const [escalation, setEscalation] = useState(null);
  const [review, setReview] = useState(null);
  const [optin, setOptin] = useState(false);
  const [agree, setAgree] = useState(false);
  const [deliverNow, setDeliverNow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [confirm13, setConfirm13] = useState(false);

  useEffect(() => { (async () => {
    try { const { data } = await api.get(`/quick-plays/${slug}`); setCfg(data);
      const pid = `quick_play_${slug.replace(/-/g, "_")}`;
      trackProductView(api, pid);
      trackEvent("view_item", { currency: "USD", value: (data.price_cents || 0) / 100, items: [{ item_id: pid, item_name: data.title }] });
      if (data.purchaser_types.length === 1) { setPurchaser(data.purchaser_types[0]); }
      else { setStep("who"); }
    }
    catch { setErr("This Quick Play isn't available."); }
  })(); }, [slug]);
  useEffect(() => { if (cfg) trackEvent("quick_play_selected", { slug }); }, [cfg, slug]);

  if (err) return <div className="max-w-3xl mx-auto px-5 py-20"><p className="text-[color:var(--vt-body-on-cream)]">{err}</p></div>;
  if (!cfg) return <div className="max-w-3xl mx-auto px-5 py-20"><p className="text-[color:var(--vt-body-on-cream)]">Loading…</p></div>;

  const set = (k, v) => setInputs((p) => ({ ...p, [k]: v }));
  const missing = cfg.input_schema.filter((f) => f.required && (inputs[f.key] == null || inputs[f.key] === "" || (Array.isArray(inputs[f.key]) && !inputs[f.key].length)));

  const submitForm = async () => {
    if (missing.length) { setErr("Please answer the required questions."); return; }
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/quick-plays/prescreen", { slug, inputs, purchaser_type: purchaser || cfg.purchaser_types[0] });
      if (!data.ok) { setEscalation(data.escalation); setStep("escalation"); }
      else { setReview(data); setStep("review"); }
    } catch (e) { setErr("Something went wrong. Try again."); } finally { setBusy(false); }
  };

  const pay = async () => {
    setBusy(true);
    trackEvent("checkout_started", { slug });
    trackEvent("begin_checkout", { currency: "USD", value: (review?.price_cents || 0) / 100, items: [{ item_id: `quick_play_${slug.replace(/-/g, "_")}` }] });
    try {
      const { data } = await api.post("/quick-plays/checkout", { order_id: review.order_id, sideline_opt_in: optin, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { setErr("Couldn't start checkout. Try again."); setBusy(false); }
  };

  return (
    <div data-testid="quick-play-page">
      <Seo title={cfg.title} description={cfg.short_description} path={`/quick-plays/${slug}`} noindex />
      <section className="bg-[color:var(--vt-dark)] vt-grain">
        <div className="max-w-3xl mx-auto px-5 lg:px-8 py-10">
          <span className="vt-eyebrow">Quick Play {cfg.number} · {cfg.audience_label}</span>
          <h1 className="mt-2 font-head text-[color:var(--vt-white)] text-4xl lg:text-6xl leading-[0.9]">{cfg.title}</h1>
        </div>
      </section>

      <section className="vt-paper">
        <div className="max-w-3xl mx-auto px-5 lg:px-8 py-12">

          {step === "escalation" && escalation && (
            <div data-testid="escalation-screen" className="border-2 border-[color:var(--vt-dark)] bg-[#FBF9F5] p-8">
              <h2 className="font-head text-[color:var(--vt-body-on-cream)] text-3xl lg:text-4xl leading-tight">{escalation.title}</h2>
              <p className="mt-4 text-[color:var(--vt-body-on-cream)] text-lg leading-relaxed">{escalation.body}</p>
              <p className="mt-6 text-[color:var(--vt-label-on-cream)] text-sm">{escalation.footer}</p>
            </div>
          )}

          {step === "who" && (
            <div data-testid="who-screen" className="space-y-5">
              <h2 className="font-head text-[color:var(--vt-body-on-cream)] text-3xl lg:text-4xl">WHO IS THIS FOR?</h2>
              <button data-testid="who-parent" onClick={() => { setPurchaser("parent"); setStep("form"); }}
                className="w-full text-left border-2 border-[color:var(--vt-dark)] hover:border-[color:var(--vt-amber)] p-5 transition-colors">
                <span className="font-head text-xl text-[color:var(--vt-body-on-cream)] uppercase">My athlete</span>
                <span className="block text-sm text-[color:var(--vt-graphite)]">I'm their parent or guardian</span>
              </button>
              <div className="border-2 border-[color:var(--vt-dark)] p-5">
                <span className="font-head text-xl text-[color:var(--vt-body-on-cream)] uppercase">Me — I'm 13 or older</span>
                <span className="block text-sm text-[color:var(--vt-graphite)] mb-3">I'm the athlete</span>
                <label className="flex items-start gap-2 text-sm text-[color:var(--vt-body-on-cream)] cursor-pointer">
                  <input type="checkbox" data-testid="confirm-13" checked={confirm13} onChange={(e) => setConfirm13(e.target.checked)} className="mt-1" />
                  I confirm I'm 13 or older.
                </label>
                <button data-testid="who-player" disabled={!confirm13} onClick={() => { setPurchaser("player"); setStep("form"); }}
                  className="mt-3 vt-btn vt-btn-primary vt-btn-sm disabled:opacity-50">Continue →</button>
              </div>
              <p className="text-xs text-[color:var(--vt-graphite)]">Under 13? Ask a parent or guardian to set this up for you.</p>
            </div>
          )}

          {step === "form" && (
            <div className="space-y-7">
              {cfg.purchaser_types.length > 1 && (
                <div>
                  <label className="vt-eyebrow vt-eyebrow--cream block mb-2">Who is this for?</label>
                  <div className="flex flex-wrap gap-2">
                    <button type="button" onClick={() => setPurchaser("parent")} data-testid="purchaser-parent" className={`font-head uppercase text-xs px-4 py-2 border-2 ${purchaser === "parent" ? "bg-[color:var(--vt-dark)] text-[color:var(--vt-cream)] border-[color:var(--vt-dark)]" : "border-[color:var(--vt-dark)] text-[color:var(--vt-body-on-cream)]"}`}>My athlete (I'm their parent/guardian)</button>
                    <button type="button" onClick={() => setPurchaser("player")} data-testid="purchaser-player" className={`font-head uppercase text-xs px-4 py-2 border-2 ${purchaser === "player" ? "bg-[color:var(--vt-dark)] text-[color:var(--vt-cream)] border-[color:var(--vt-dark)]" : "border-[color:var(--vt-dark)] text-[color:var(--vt-body-on-cream)]"}`}>Me — I'm 13 or older</button>
                  </div>
                </div>
              )}
              {cfg.input_schema.map((f) => (
                <div key={f.key}>
                  <label className="vt-eyebrow vt-eyebrow--cream block mb-2">{f.label}{f.required && <span className="text-[color:var(--vt-amber)]"> *</span>}</label>
                  <Field f={f} value={inputs[f.key]} onChange={(v) => set(f.key, v)} />
                </div>
              ))}
              {err && <p className="text-sm text-[#C0492B]" data-testid="form-error">{err}</p>}
              <button onClick={submitForm} disabled={busy} data-testid="form-continue" className="vt-btn vt-btn-secondary-cream w-full sm:w-auto">
                {busy ? "Checking…" : "Continue →"}
              </button>
            </div>
          )}

          {step === "review" && review && (
            <div data-testid="review-screen">
              <span className="vt-eyebrow vt-eyebrow--cream">Review</span>
              <h2 className="mt-2 font-head text-[color:var(--vt-body-on-cream)] text-4xl">HERE'S WHAT YOU'LL GET</h2>
              <ul className="mt-6 space-y-3">
                {review.outline.map((s) => (
                  <li key={s.num} className="flex gap-3 border-b border-[color:var(--vt-dark)]/15 pb-3">
                    <span className="font-[IBM_Plex_Mono] text-[color:var(--vt-amber)]">{s.num}</span>
                    <span className="font-head text-lg text-[color:var(--vt-body-on-cream)]">{s.title}</span>
                  </li>
                ))}
              </ul>
              <label className="mt-6 flex items-start gap-3 text-sm text-[color:var(--vt-body-on-cream)]" data-testid="optin-sideline">
                <input type="checkbox" checked={optin} onChange={(e) => setOptin(e.target.checked)} className="mt-1" />
                Also send me The Sideline (weekly, free).
              </label>
              <label className="mt-3 flex items-start gap-3 text-sm text-[color:var(--vt-body-on-cream)]" data-testid="agree-disclaimer">
                <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-1" />
                I understand Quick Plays are coaching/parenting guidance, not medical, mental-health, or legal advice.
              </label>
              <label className="mt-3 flex items-start gap-3 text-sm text-[color:var(--vt-body-on-cream)]" data-testid="agree-delivery">
                <input type="checkbox" checked={deliverNow} onChange={(e) => setDeliverNow(e.target.checked)} className="mt-1" />
                I want my Quick Play delivered immediately and agree that, once delivered, the sale is final (waiving any right of withdrawal). See our <a href="/legal/terms" target="_blank" rel="noreferrer" className="link-line">Terms</a> & <a href="/legal/refund" target="_blank" rel="noreferrer" className="link-line">Refund Policy</a>.
              </label>
              <p className="mt-4 font-[IBM_Plex_Mono] text-xs uppercase tracking-[0.15em] text-[color:var(--vt-label-on-cream)]">Digital product — all sales final once delivered.</p>
              <p data-testid="statement-descriptor-note" className="mt-1 text-xs text-[color:var(--vt-body-on-cream)] opacity-70">Appears on your card statement as LINK.COM* VOLUNTOLDHQ QP.</p>
              {err && <p className="mt-2 text-sm text-[#C0492B]">{err}</p>}
              <div className="mt-5 flex items-center gap-4">
                <button onClick={pay} disabled={busy || !agree || !deliverNow} data-testid="pay-button" className="vt-btn vt-btn-primary disabled:opacity-50">
                  {busy ? "Starting…" : `Pay ${money(review.price_cents)} →`}
                </button>
                <button onClick={() => setStep("form")} className="link-line text-sm text-[color:var(--vt-body-on-cream)]">← Edit answers</button>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
